/**
 * InlineCopyableText - Displays text inline with click-to-copy functionality
 *
 * Unlike CopyableValue which uses icons + tooltips, this component shows
 * the actual text inline with clear visual feedback on copy and a custom
 * hover tooltip for showing full text.
 *
 * @example
 * ```tsx
 * <InlineCopyableText text="Transaction completed successfully" />
 * ```
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { copyToClipboard, getCheckmarkIcon } from './utils';

// Flash animation for copy success
const copyFlash = keyframes`
  0% { background-color: rgba(34, 197, 94, 0.25); }
  100% { background-color: transparent; }
`;

// Fade in/out animation for copied badge
const fadeInOut = keyframes`
  0% { opacity: 0; transform: translateX(-4px); }
  15% { opacity: 1; transform: translateX(0); }
  85% { opacity: 1; }
  100% { opacity: 0; }
`;

const TextWrapper = styled.span<{
  $copied: boolean;
  $disabled: boolean;
  $maxWidth?: string;
}>`
  display: inline-flex;
  align-items: center;
  max-width: ${props => props.$maxWidth || '100%'};
  cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  padding: 0.25rem 0.5rem;
  margin: -0.25rem -0.5rem;
  border-radius: 0.25rem;
  transition: background-color 0.15s ease, color 0.15s ease;

  ${props => props.$copied && css`
    animation: ${copyFlash} 1.2s ease-out;
    color: #16a34a;
    font-weight: 600;
  `}

  ${props => !props.$disabled && !props.$copied && css`
    &:hover {
      background-color: rgba(99, 102, 241, 0.08);
    }

    &:active {
      background-color: rgba(99, 102, 241, 0.15);
    }
  `}
`;

const TextContent = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CopiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  margin-left: 0.5rem;
  padding: 0.15rem 0.4rem;
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  color: #16a34a;
  border-radius: 0.25rem;
  font-size: 0.65rem;
  font-weight: 600;
  white-space: nowrap;
  animation: ${fadeInOut} 1.2s ease-out forwards;
  flex-shrink: 0;

  svg {
    width: 10px;
    height: 10px;
  }
`;

const EmptyText = styled.span`
  font-style: italic;
  color: #94a3b8;
`;

// Custom hover tooltip (portal-rendered)
const HoverTooltip = styled.div<{
  $visible: boolean;
  $position: { top: number; left: number };
}>`
  position: fixed;
  top: ${props => props.$position.top}px;
  left: ${props => props.$position.left}px;
  max-width: 400px;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  line-height: 1.5;
  z-index: 99999;
  pointer-events: none;
  opacity: ${props => props.$visible ? 1 : 0};
  transform: translateY(${props => props.$visible ? 0 : 4}px);
  transition: opacity 0.15s ease, transform 0.15s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  word-wrap: break-word;
  white-space: normal;

  &::after {
    content: '';
    position: absolute;
    top: -5px;
    left: 20px;
    width: 10px;
    height: 10px;
    background: #1e293b;
    transform: rotate(45deg);
  }
`;

interface InlineCopyableTextProps {
  /** Text to display and copy */
  text?: string;
  /** Text shown when empty (default: "no summary") */
  emptyText?: string;
  /** Max width for text truncation */
  maxWidth?: string;
  /** Callback fired after successful copy */
  onCopy?: (text: string) => void;
  /** Additional CSS class */
  className?: string;
}

export const InlineCopyableText: React.FC<InlineCopyableTextProps> = ({
  text,
  emptyText = 'no summary',
  maxWidth,
  onCopy,
  className,
}) => {
  const [copied, setCopied] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const textRef = useRef<HTMLSpanElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!text) return;

    const success = await copyToClipboard(text);
    if (success) {
      setCopied(true);
      setShowTooltip(false); // Hide tooltip when copying
      onCopy?.(text);
      setTimeout(() => setCopied(false), 1200);
    }
  }, [text, onCopy]);

  const handleMouseEnter = useCallback(() => {
    if (!text || copied) return;

    hoverTimeoutRef.current = setTimeout(() => {
      if (textRef.current) {
        const rect = textRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        // Calculate left position, ensuring tooltip doesn't go off-screen
        let left = rect.left;
        if (left + 400 > viewportWidth - 10) {
          left = Math.max(10, viewportWidth - 410);
        }

        setTooltipPosition({
          top: rect.bottom + 8,
          left: Math.max(10, left)
        });
        setShowTooltip(true);
      }
    }, 300);
  }, [text, copied]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setShowTooltip(false);
  }, []);

  if (!text) {
    return <EmptyText className={className}>{emptyText}</EmptyText>;
  }

  return (
    <>
      <TextWrapper
        ref={textRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        $copied={copied}
        $disabled={!text}
        $maxWidth={maxWidth}
        className={className}
      >
        <TextContent>{text}</TextContent>
        {copied && (
          <CopiedBadge>
            {getCheckmarkIcon()}
            copied!
          </CopiedBadge>
        )}
      </TextWrapper>

      {/* Custom hover tooltip via portal */}
      {showTooltip && text && !copied && ReactDOM.createPortal(
        <HoverTooltip $visible={showTooltip} $position={tooltipPosition}>
          {text}
        </HoverTooltip>,
        document.body
      )}
    </>
  );
};

export default InlineCopyableText;
